import appointment from "../../../../services/appointment";
import { WIZARD_DELAYS } from "../constants";
import { BaseStepDependencies } from "./stepTypes";

interface Service {
  name: string;
  duration_minutes: number;
}

interface ServicesStepDependencies extends BaseStepDependencies {
  addUserMessage: (content: string) => void;
}

const SERVICE_SUGGESTIONS = [
  { name: "Consulta Individual", duration: 60 },
  { name: "Aula Particular", duration: 50 },
  { name: "Sessão de Terapia", duration: 60 },
  { name: "Avaliação Inicial", duration: 90 },
  { name: "Retorno", duration: 30 },
  { name: "Atendimento Express", duration: 30 },
];

// Estado temporário dos serviços sendo configurados
let currentServices: Service[] = [];

export const startServicesConfiguration = (deps: ServicesStepDependencies) => {
  const { addBotMessage } = deps;

  // Reset services
  currentServices = [];

  setTimeout(() => {
    addBotMessage(
      "Agora vamos configurar seus serviços! 💼\n\nQuais tipos de atendimento você oferece?",
      { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, 800);

  setTimeout(() => {
    addBotMessage(
      "Vou te mostrar algumas sugestões. Escolha as que se aplicam:",
      {
        wizardType: "question",
        delay: WIZARD_DELAYS.MEDIUM,
        actions: SERVICE_SUGGESTIONS.slice(0, 3).map((service) => ({
          label: `${service.name} (${service.duration}min)`,
          onClick: () => handleServiceSelection(service, deps),
          variant: "secondary" as const,
        })),
      },
    );
  }, WIZARD_DELAYS.LONG);

  setTimeout(() => {
    addBotMessage("Ou prefere adicionar manualmente?", {
      wizardType: "question",
      delay: WIZARD_DELAYS.MEDIUM,
      actions: [
        {
          label: "Adicionar Manualmente",
          onClick: () => handleManualService(deps),
          variant: "primary" as const,
        },
        {
          label: "Pular por Enquanto",
          onClick: () => handleSkipServices(deps),
          variant: "secondary" as const,
        },
      ],
    });
  }, WIZARD_DELAYS.VERY_LONG);
};

const handleServiceSelection = (
  service: { name: string; duration: number },
  deps: ServicesStepDependencies,
) => {
  const { addUserMessage, addBotMessage } = deps;

  addUserMessage(`${service.name} (${service.duration}min)`);

  currentServices.push({
    name: service.name,
    duration_minutes: service.duration,
  });

  setTimeout(() => {
    addBotMessage(
      `✅ Ótimo! "${service.name}" adicionado.\n\nDeseja adicionar mais algum serviço?`,
      {
        wizardType: "question",
        delay: WIZARD_DELAYS.MEDIUM,
        actions: [
          {
            label: "Sim, Adicionar Mais",
            onClick: () => showMoreServiceOptions(deps),
            variant: "primary" as const,
          },
          {
            label: "Não, Finalizar",
            onClick: () => void saveServicesAndContinue(deps),
            variant: "secondary" as const,
          },
        ],
      },
    );
  }, WIZARD_DELAYS.MEDIUM);
};

const showMoreServiceOptions = (deps: ServicesStepDependencies) => {
  const { addUserMessage, addBotMessage } = deps;

  addUserMessage("Sim, adicionar mais");

  const remainingSuggestions = SERVICE_SUGGESTIONS.filter(
    (s) => !currentServices.some((cs) => cs.name === s.name),
  );

  if (remainingSuggestions.length > 0) {
    setTimeout(() => {
      addBotMessage("Aqui estão mais opções:", {
        wizardType: "question",
        delay: WIZARD_DELAYS.MEDIUM,
        actions: [
          ...remainingSuggestions.slice(0, 3).map((service) => ({
            label: `${service.name} (${service.duration}min)`,
            onClick: () => handleServiceSelection(service, deps),
            variant: "secondary" as const,
          })),
          {
            label: "Adicionar Manualmente",
            onClick: () => handleManualService(deps),
            variant: "primary" as const,
          },
        ],
      });
    }, WIZARD_DELAYS.MEDIUM);
  } else {
    handleManualService(deps);
  }
};

const handleManualService = (deps: ServicesStepDependencies) => {
  const { addUserMessage, addBotMessage, setAwaitingUserInput, advanceToStep } =
    deps;

  addUserMessage("Adicionar manualmente");

  setTimeout(() => {
    addBotMessage(
      "Perfeito! Digite o nome do serviço que você oferece.\n\nExemplo: Consulta Nutricional",
      { wizardType: "question", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("service_name", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

export const handleServiceNameInput = (
  serviceName: string,
  deps: ServicesStepDependencies,
) => {
  const { addBotMessage, setAwaitingUserInput, advanceToStep } = deps;

  // Validações do nome do serviço
  const trimmedName = serviceName.trim();

  // Validação: nome não pode estar vazio
  if (!trimmedName) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome do serviço não pode estar vazio. Por favor, digite o nome do serviço que você oferece.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: nome deve ter pelo menos 3 caracteres
  if (trimmedName.length < 3) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome do serviço deve ter pelo menos 3 caracteres. Por favor, digite um nome válido.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: nome não deve ter mais de 100 caracteres
  if (trimmedName.length > 100) {
    setTimeout(() => {
      addBotMessage(
        "❌ O nome do serviço não pode ter mais de 100 caracteres. Por favor, use um nome mais curto.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Salva o nome temporariamente
  (
    globalThis as typeof globalThis & { __temp_service_name: string }
  ).__temp_service_name = trimmedName;

  setTimeout(() => {
    addBotMessage(
      `Ótimo! "${trimmedName}"\n\nQual a duração média deste atendimento em minutos?\n\nExemplo: 60`,
      { wizardType: "question", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("service_duration", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

export const handleServiceDurationInput = (
  duration: string,
  deps: ServicesStepDependencies,
) => {
  const { addBotMessage, setAwaitingUserInput, advanceToStep } = deps;

  const serviceName = (
    globalThis as typeof globalThis & { __temp_service_name: string }
  ).__temp_service_name;

  // Validação: remove espaços e verifica se está vazio
  const trimmedDuration = duration.trim();
  if (!trimmedDuration) {
    setTimeout(() => {
      addBotMessage(
        "❌ A duração não pode estar vazia. Por favor, digite um número de minutos.\n\nExemplo: 60",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  const durationNum = Number.parseInt(trimmedDuration, 10);

  // Validação: deve ser um número válido
  if (Number.isNaN(durationNum)) {
    setTimeout(() => {
      addBotMessage(
        "❌ Por favor, digite apenas números para a duração.\n\nExemplo: 60",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: duração deve ser maior que zero
  if (durationNum <= 0) {
    setTimeout(() => {
      addBotMessage("❌ A duração deve ser maior que zero.\n\nExemplo: 60", {
        wizardType: "error",
        delay: WIZARD_DELAYS.MEDIUM,
      });
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: duração mínima de 5 minutos
  if (durationNum < 5) {
    setTimeout(() => {
      addBotMessage(
        "❌ A duração mínima é de 5 minutos. Por favor, digite um valor maior.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  // Validação: duração máxima de 480 minutos (8 horas)
  if (durationNum > 480) {
    setTimeout(() => {
      addBotMessage(
        "❌ A duração máxima é de 480 minutos (8 horas). Por favor, digite um valor menor.",
        { wizardType: "error", delay: WIZARD_DELAYS.MEDIUM },
      );
    }, WIZARD_DELAYS.MEDIUM);
    setTimeout(() => {
      setAwaitingUserInput(true);
    }, WIZARD_DELAYS.LONG);
    return;
  }

  currentServices.push({
    name: serviceName,
    duration_minutes: durationNum,
  });

  setTimeout(() => {
    advanceToStep("services_confirmation", 0);
    addBotMessage(
      `✅ Serviço "${serviceName}" (${durationNum}min) adicionado!\n\nDeseja adicionar mais algum serviço?`,
      {
        wizardType: "question",
        delay: WIZARD_DELAYS.MEDIUM,
        actions: [
          {
            label: "Sim, Adicionar Mais",
            onClick: () => continueAddingServices(deps),
            variant: "primary" as const,
          },
          {
            label: "Não, Finalizar",
            onClick: () => void saveServicesAndContinue(deps),
            variant: "secondary" as const,
          },
        ],
      },
    );
  }, WIZARD_DELAYS.MEDIUM);
};

const continueAddingServices = (deps: ServicesStepDependencies) => {
  const { addUserMessage, addBotMessage, setAwaitingUserInput, advanceToStep } =
    deps;

  addUserMessage("Sim, adicionar mais");

  setTimeout(() => {
    addBotMessage(
      "Perfeito! Digite o nome do próximo serviço que você oferece.\n\nExemplo: Consulta Nutricional",
      { wizardType: "question", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("service_name", 0);
    setAwaitingUserInput(true);
  }, WIZARD_DELAYS.LONG);
};

const handleSkipServices = (deps: ServicesStepDependencies) => {
  const { addUserMessage, addBotMessage, advanceToStep } = deps;

  addUserMessage("Pular por enquanto");

  setTimeout(() => {
    addBotMessage(
      "Sem problemas! Você pode adicionar seus serviços depois no painel.",
      { wizardType: "info", delay: WIZARD_DELAYS.MEDIUM },
    );
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(() => {
    advanceToStep("availability", 0);
    // Importar e chamar o próximo step
    void import("./availabilityStep").then((module) => {
      module.startAvailabilityConfiguration(deps);
    });
  }, WIZARD_DELAYS.LONG);
};

const saveServicesAndContinue = async (deps: ServicesStepDependencies) => {
  const {
    userId,
    addUserMessage,
    addBotMessage,
    advanceToStep,
    setIsProcessing,
  } = deps;

  addUserMessage("Não, finalizar");

  if (currentServices.length === 0) {
    handleSkipServices(deps);
    return;
  }

  setTimeout(() => {
    addBotMessage("Perfeito! Salvando seus serviços...", {
      wizardType: "info",
      delay: WIZARD_DELAYS.MEDIUM,
    });
  }, WIZARD_DELAYS.MEDIUM);

  setTimeout(async () => {
    if (!userId) {
      addBotMessage(
        "❌ Erro: usuário não identificado. Vamos pular por enquanto.",
        {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        },
      );
      setTimeout(() => {
        handleSkipServices(deps);
      }, WIZARD_DELAYS.LONG);
      return;
    }

    setIsProcessing(true);

    try {
      const result = await appointment.setProfessionalServices(
        String(userId),
        currentServices,
      );

      if (result) {
        addBotMessage(
          `✅ Serviços salvos com sucesso!\n\nTotal: ${currentServices.length} serviço(s) configurado(s).`,
          { wizardType: "confirmation", delay: WIZARD_DELAYS.MEDIUM },
        );

        setTimeout(() => {
          addBotMessage("Agora vamos configurar sua disponibilidade! 📅", {
            wizardType: "info",
            delay: WIZARD_DELAYS.MEDIUM,
          });
        }, WIZARD_DELAYS.LONG);

        setTimeout(() => {
          advanceToStep("availability", 0);
          // Importar e chamar o próximo step
          void import("./availabilityStep").then((module) => {
            module.startAvailabilityConfiguration(deps);
          });
        }, WIZARD_DELAYS.VERY_LONG);
      } else {
        addBotMessage("❌ Erro ao salvar serviços. Vamos pular por enquanto.", {
          wizardType: "error",
          delay: WIZARD_DELAYS.MEDIUM,
        });
        handleSkipServices(deps);
      }
    } catch (error) {
      console.error("Erro ao salvar serviços:", error);
      addBotMessage("❌ Erro ao salvar serviços. Vamos pular por enquanto.", {
        wizardType: "error",
        delay: WIZARD_DELAYS.MEDIUM,
      });
      handleSkipServices(deps);
    } finally {
      setIsProcessing(false);
    }
  }, WIZARD_DELAYS.LONG);
};
